import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCopyToClipboard } from "~/components/CopyToClipboard";
import { CopyIcon } from "lucide-react";
import { Tooltip } from "~/components/Tooltip";

// // // //

/**
 * CopyKeyButton
 * Copies an API Key to the user's clipboard
 */
function CopyButton(props: { text: string }) {
    const { text } = props;

    // Setup CopyToClipboard hook w/ useState to dipslay "Copied" message
    const [copied, setCopied] = useState<boolean>(false);
    const [copyToClipboard] = useCopyToClipboard(() => {
        setCopied(true);
    });

    useEffect(() => {
        if (copied === true) {
            setTimeout(() => {
                setCopied(false);
            }, 1000);
        }
    }, [copied]);

    const message: string = copied ? "Copied!" : "Copy to Clipboard";

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="w-40 px-2 py-1 text-center text-xs">
                    <p className="whitespace-nowrap">{message}</p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <button
                    ref={setTriggerRef}
                    onClick={() => {
                        copyToClipboard(text);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                >
                    <CopyIcon className="size-3" />
                </button>
            )}
        </Tooltip>
    );
}

/**
 * RevokeConfirmDialog
 * Destructive-action confirmation that requires the user to type the client's
 * name before the confirm button is enabled, matching the type-to-confirm
 * pattern used elsewhere in the app.
 */
export function RevokeConfirmDialog(props: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientName: string;
    loading?: boolean;
    title?: string;
    description?: React.ReactNode;
    confirmLabel?: string;
    onConfirm: () => void;
}) {
    const {
        open,
        onOpenChange,
        clientName,
        loading,
        title = "Revoke OAuth client",
        description,
        confirmLabel = "Revoke client",
        onConfirm,
    } = props;

    const [typed, setTyped] = useState("");
    const matches = typed.trim() === clientName.trim();

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) setTyped("");
                onOpenChange(next);
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {description ?? (
                            <>
                                This revokes the client and invalidates all of
                                its secrets and active sessions. This cannot be
                                undone.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <Label htmlFor="revoke-confirm-name" className="text-sm">
                        Type{" "}
                        <span className="font-mono font-semibold">
                            {clientName}
                        </span>{" "}
                        <CopyButton text={clientName} /> to confirm.
                    </Label>
                    <Input
                        id="revoke-confirm-name"
                        value={typed}
                        autoComplete="off"
                        onChange={(event) => setTyped(event.target.value)}
                    />
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={!matches || loading}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
